import {
  FF_MANDATORY_ACCOUNT_CHINA,
  FF_MANDATORY_ACCOUNT_CHINA_VERSION,
  FF_MANDATORY_ACCOUNT_INDIA,
  FF_MANDATORY_ACCOUNT_INDIA_VERSION,
  PAYEX_PLATFORM,
} from 'constants/index';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getPlatform,
  getRecipientCurrencyCode,
  isPayingFromChina,
  isPayingFromIndia,
  paymentIsFromAgents,
} from 'selectors';
import {
  TestVariant,
  getTestVariant,
} from 'utils/getTestVariant/getTestVariant';

type Eligibility = {
  india: boolean;
  china: boolean;
};

type TestVariants = {
  india: TestVariant;
  china: TestVariant;
};

export type UseMandatoryAccountEligibilityResult = {
  eligibility: Eligibility;
  testVariants: TestVariants;
  isFetching: boolean;
};

const REFETCH_TIMEOUT = 60000;
export const DISCARDED_RECIPIENT_CURRENCIES = [
  'SGD',
  'JPY',
  'KRW',
  'MYR',
  'HKD',
];

export function useMandatoryAccountEligibility(): UseMandatoryAccountEligibilityResult {
  const isFromAgents = useSelector(paymentIsFromAgents);
  const isPayexPlatform = useSelector(getPlatform) === PAYEX_PLATFORM;
  const payingFromIndia = useSelector(isPayingFromIndia);
  const payingFromChina = useSelector(isPayingFromChina);
  const recipientCurrency = useSelector(getRecipientCurrencyCode);

  const eligibility = useMemo(
    () => ({
      india: !isFromAgents && isPayexPlatform && payingFromIndia,
      china:
        !isFromAgents &&
        isPayexPlatform &&
        payingFromChina &&
        !!recipientCurrency &&
        !DISCARDED_RECIPIENT_CURRENCIES.includes(recipientCurrency),
    }),
    [
      isFromAgents,
      isPayexPlatform,
      payingFromIndia,
      payingFromChina,
      recipientCurrency,
    ],
  );

  const { data: featureFlags } = usePeertransferFlags(
    [
      FF_MANDATORY_ACCOUNT_INDIA,
      FF_MANDATORY_ACCOUNT_INDIA_VERSION,
      FF_MANDATORY_ACCOUNT_CHINA,
      FF_MANDATORY_ACCOUNT_CHINA_VERSION,
    ],
    {
      refetchInterval: REFETCH_TIMEOUT,
      enabled: eligibility.india || eligibility.china,
    },
  );

  const testVariants = useMemo(
    () => ({
      india: getTestVariant(
        featureFlags?.[FF_MANDATORY_ACCOUNT_INDIA] ?? false,
        featureFlags?.[FF_MANDATORY_ACCOUNT_INDIA_VERSION] ?? false,
      ),
      china: getTestVariant(
        featureFlags?.[FF_MANDATORY_ACCOUNT_CHINA] ?? false,
        featureFlags?.[FF_MANDATORY_ACCOUNT_CHINA_VERSION] ?? false,
      ),
    }),
    [featureFlags],
  );

  const isFetching = featureFlags === undefined;

  return { eligibility, testVariants, isFetching };
}
